import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Check, Folder } from 'lucide-react';

export const SearchableProjectSelect = ({
  projects = [],
  value = '',
  onChange,
  placeholder = '-- Select Project --',
  required = false,
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find currently selected project object
  const selectedProject = projects.find(
    (p) => String(p.id || p._id) === String(value)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter projects by name or code (case-insensitive)
  const filteredProjects = projects.filter((p) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const name = (p.name || p.project_name || '').toLowerCase();
    const code = (p.code || p.project_code || '').toLowerCase();
    return name.includes(q) || code.includes(q);
  });

  const handleSelect = (project) => {
    if (onChange) {
      onChange(project);
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(null);
    }
    setSearchQuery('');
  };

  // Format option label
  const formatLabel = (p) => {
    const name = p.name || p.project_name || 'Project';
    const code = p.code || p.project_code || 'N/A';
    return `${name} (Code: ${code})`;
  };

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Hidden native input for HTML5 required form validation */}
      <input
        type="text"
        tabIndex={-1}
        className="opacity-0 absolute pointer-events-none h-0 w-0"
        value={value || ''}
        required={required}
        onChange={() => {}}
      />

      {/* Main Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-left flex items-center justify-between gap-2 outline-none transition-all ${
          isOpen ? 'border-blue-500 ring-2 ring-blue-100 bg-white' : 'hover:border-slate-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-2 min-w-0 truncate">
          <Folder className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className={`truncate ${selectedProject ? 'text-slate-900 font-bold' : 'text-slate-400 font-medium'}`}>
            {selectedProject ? formatLabel(selectedProject) : placeholder}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selectedProject && (
            <span
              onClick={handleClearSelection}
              className="p-0.5 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-200 transition-colors"
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Searchable Dropdown Popover */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search Box Header */}
          <div className="p-2 border-b border-slate-100 bg-slate-50/80">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search project name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                }}
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-400 font-medium text-slate-800"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto divide-y divide-slate-50 text-xs">
            {filteredProjects.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-xs font-semibold">
                No projects found
              </div>
            ) : (
              filteredProjects.map((p) => {
                const pId = p.id || p._id;
                const isSelected = String(pId) === String(value);
                const name = p.name || p.project_name || 'Project';
                const code = p.code || p.project_code || 'N/A';

                return (
                  <div
                    key={pId}
                    onClick={() => handleSelect(p)}
                    className={`px-3 py-2.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-2 transition-colors ${
                      isSelected ? 'bg-blue-50/90 text-blue-700 font-bold' : 'text-slate-700 font-medium'
                    }`}
                  >
                    <div className="min-w-0 truncate">
                      <span className="font-semibold text-slate-900">{name}</span>
                      <span className="ml-1.5 text-[11px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                        Code: {code}
                      </span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableProjectSelect;
